import type { DragEndEvent, Modifier } from '@dnd-kit/core';
import { DndContext, PointerSensor, useSensor } from '@dnd-kit/core';
import { SortableContext } from '@dnd-kit/sortable';
import { AddItem, FileUpload } from '@wix/design-system';
import React from 'react';

import type { LogBiFn } from './../../../../../_shared/bi-context-provider/bi-context-provider';

import {
  MEDIA_SETTINGS_ADD_MEDIA_INPUT,
  MEDIA_SETTINGS_SORTABLE_ITEMS_CONTAINER,
} from '../../../../data-hooks';
import type { GalleryItem, SelectFileFn } from '../../common-types';
import { restrictToBoundingRect } from './restrict-to-bounding-rect';
import { SortableItem } from './sortable-item';
import classes from './sortable-list-scss';

type SortableListProps = {
  items: GalleryItem[];
  onItemClick: (id: number) => void;
  onItemDoubleClick: (id: number) => void;
  onFileSelect: SelectFileFn;
  onSortEnd: ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => void;
  logBi: LogBiFn;
};

export const SortableList: React.FC<SortableListProps> = ({
  items,
  onItemClick,
  onItemDoubleClick,
  onFileSelect,
  onSortEnd,
  logBi,
}: SortableListProps) => {
  const [containerRef, setContainerRef] = React.useState<HTMLDivElement | null>(null);
  const sensors = useSensor(PointerSensor, {
    activationConstraint: {
      distance: 5,
    },
  });

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = items.findIndex(item => item.id === active.id);
      const newIndex = items.findIndex(item => item.id === over?.id);
      logBi({ dataHook: MEDIA_SETTINGS_SORTABLE_ITEMS_CONTAINER, value: newIndex });
      onSortEnd({ oldIndex, newIndex });
    }
  };

  return (
    <div className={classes.container} ref={setContainerRef}>
      <FileUpload
        multiple
        accept=".jpeg,.gif,.png"
        onChange={() => {}}
        dataHook={MEDIA_SETTINGS_ADD_MEDIA_INPUT}
        className={classes.fileUpload}
      >
        {() => <AddItem theme="image" onClick={() => onFileSelect?.()} />}
      </FileUpload>
      {containerRef && (
        <DndContext
          sensors={[sensors]}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToContainerEdges(containerRef)]}
        >
          <SortableContext items={items}>
            {items.map((item, index) => (
              <SortableItem
                key={item.id}
                item={item}
                onClick={() => onItemClick(index)}
                onDoubleClick={() => onItemDoubleClick(index)}
                className={classes.item}
              />
            ))}
          </SortableContext>
        </DndContext>
      )}
    </div>
  );
};

const restrictToContainerEdges =
  (containerRef: HTMLElement): Modifier =>
  ({ transform, draggingNodeRect }) => {
    if (!draggingNodeRect || !containerRef) {
      return transform;
    }

    return restrictToBoundingRect(
      transform,
      draggingNodeRect,
      containerRef.getBoundingClientRect()
    );
  };
